import axios from 'axios';
import type { ReplayGame } from 'react-chess-replay-trainer';
import type { GameSource } from '../common/gameSource';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import { markGameViewedInCache } from './gameViewedCache';

const replayApi = getApiInstance('/replay', 'no-settings');
const positionsApi = getApiInstance('/positions', 'no-settings');

export {
  OPENING_FULL_MOVE_LIMIT as REPLAY_OPENING_FULL_MOVE_LIMIT,
  OPENING_SAN_LIMIT as REPLAY_OPENING_SAN_LIMIT,
} from '../common/openingFormat';

/** Default replay Elo range — max is intentionally high so top games are never clipped. */
export const REPLAY_DEFAULT_MIN_ELO = 2500;
export const REPLAY_DEFAULT_MAX_ELO = 10_000;

export type ReplayGameSummary = {
  gameId: string;
  white: string;
  black: string;
  whiteElo: number;
  blackElo: number;
  result: string;
  /** First {@link REPLAY_OPENING_FULL_MOVE_LIMIT} full moves as SAN plies. */
  openingSans?: string[];
  eco?: string;
  opening?: string;
  /** Full variation name from the lichess openings reference table. */
  lichessOpening?: string;
  timeControl?: string;
  timeClass?: string;
  date?: string;
  source: GameSource;
  viewedAt?: string;
  /** False when a free-tier user cannot reopen this replay from history. */
  replayAccessible?: boolean;
};

export type ReplayHistoryResult = {
  games: ReplayGameSummary[];
  offset: number;
  total?: number;
  hasMore?: boolean;
  replayHistoryLimit?: number;
  lockedHistoryCount?: number;
};

export const fetchReplayHistory = async (params?: {
  limit?: number;
  offset?: number;
  sources?: GameSource[];
  includeTotal?: boolean;
  player?: string;
  myGames?: boolean;
}): Promise<ReplayHistoryResult> => {
  try {
    const query: Record<string, string | number | boolean> = {};
    if (params?.limit !== undefined) {
      query.limit = params.limit;
    }
    if (params?.offset !== undefined) {
      query.offset = params.offset;
    }
    if (params?.sources?.length) {
      query.sources = params.sources.join(',');
    }
    if (params?.includeTotal) {
      query.includeTotal = true;
    }
    if (params?.player?.trim()) {
      query.player = params.player.trim();
    }
    if (params?.myGames) {
      query.myGames = true;
    }

    const { data } = await replayApi.get<{
      games: ReplayGameSummary[];
      offset: number;
      total?: number;
      hasMore?: boolean;
      replayHistoryLimit?: number;
      lockedHistoryCount?: number;
    }>('/history', { params: query });
    return {
      games: data.games,
      offset: data.offset ?? params?.offset ?? 0,
      total: data.total,
      hasMore: data.hasMore,
      replayHistoryLimit: data.replayHistoryLimit,
      lockedHistoryCount: data.lockedHistoryCount,
    };
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load replay history' }),
    );
  }
};

export type TopUnviewedReplayGamesResult = {
  games: ReplayGameSummary[];
  offset: number;
  total?: number;
  hasMore?: boolean;
};

export type UnviewedReplayGamesSort = 'top' | 'any';

export const fetchTopUnviewedReplayGames = async (params?: {
  limit?: number;
  offset?: number;
  minElo?: number;
  maxElo?: number;
  sources?: GameSource[];
  includeTotal?: boolean;
  /** `top` (default): highest-rated first. `any`: random sample for fast loads. */
  sort?: UnviewedReplayGamesSort;
  player?: string;
  myGames?: boolean;
}): Promise<TopUnviewedReplayGamesResult> => {
  try {
    const query: Record<string, string | number | boolean> = {};
    if (params?.limit !== undefined) {
      query.limit = params.limit;
    }
    if (params?.offset !== undefined) {
      query.offset = params.offset;
    }
    if (params?.minElo !== undefined) {
      query.minElo = params.minElo;
    }
    if (params?.maxElo !== undefined) {
      query.maxElo = params.maxElo;
    }
    if (params?.sources?.length) {
      query.sources = params.sources.join(',');
    }
    if (params?.includeTotal) {
      query.includeTotal = true;
    }
    if (params?.sort === 'any') {
      query.sort = 'any';
    }
    if (params?.player?.trim()) {
      query.player = params.player.trim();
    }
    if (params?.myGames) {
      query.myGames = true;
    }

    const { data } = await replayApi.get<{
      games: ReplayGameSummary[];
      offset: number;
      total?: number;
      hasMore?: boolean;
    }>('/top-unviewed', { params: query });
    return {
      games: data.games,
      offset: data.offset ?? params?.offset ?? 0,
      total: data.total,
      hasMore: data.hasMore,
    };
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, {
        action: 'Failed to load top unviewed games',
      }),
    );
  }
};

export const fetchReplayGame = async (
  gameId: string,
): Promise<ReplayGame | null> => {
  try {
    const { data } = await positionsApi.get<ReplayGame>(
      `/games/${encodeURIComponent(gameId)}`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load game replay' }),
    );
  }
};

export type RecordReplayHalfMoveSeen = (
  gameId: string,
  halfMove: number,
) => Promise<{ seenBefore: boolean }>;

/**
 * Record that the user reached a half move while replaying. Returns whether that
 * half move was already seen before this visit.
 */
export const recordReplayHalfMoveSeen: RecordReplayHalfMoveSeen = async (
  gameId: string,
  halfMove: number,
): Promise<{ seenBefore: boolean }> => {
  if (!gameId.trim() || !Number.isInteger(halfMove) || halfMove < 1) {
    return { seenBefore: false };
  }

  try {
    const { data, status } = await replayApi.post<{ seenBefore: boolean }>(
      `/views/${encodeURIComponent(gameId)}/plies`,
      { halfMove },
      {
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 401,
      },
    );
    if (status === 401) {
      return { seenBefore: false };
    }
    return { seenBefore: data?.seenBefore === true };
  } catch {
    return { seenBefore: false };
  }
};

export const fetchReplayAccess = async (
  gameId: string,
): Promise<{ viewed: boolean; accessible: boolean }> => {
  if (!gameId.trim()) {
    return { viewed: false, accessible: true };
  }

  try {
    const { data, status } = await replayApi.get<{
      viewed: boolean;
      accessible: boolean;
    }>(`/views/${encodeURIComponent(gameId)}`, {
      skipGlobalErrorToast: true,
      validateStatus: (statusCode) => statusCode === 200 || statusCode === 401,
    });
    if (status === 401) {
      return { viewed: false, accessible: true };
    }
    return {
      viewed: data.viewed === true,
      accessible: data.accessible !== false,
    };
  } catch {
    return { viewed: false, accessible: true };
  }
};

/** Record that the user opened a game for replay (upserts viewedAt server-side). */
export const recordReplayView = async (gameId: string): Promise<void> => {
  if (!gameId.trim()) {
    return;
  }

  try {
    const { status, data } = await replayApi.post<{ gameId: string }>(
      '/views',
      { gameId },
      {
        skipGlobalErrorToast: true,
        validateStatus: (statusCode) =>
          statusCode === 201 || statusCode === 401 || statusCode === 429,
      },
    );
    if (status === 401) {
      return;
    }
    if (status === 429) {
      const message =
        typeof data === 'object' &&
        data != null &&
        'message' in data &&
        typeof data.message === 'string'
          ? data.message
          : 'You cannot open this replay game right now.';
      const code =
        typeof data === 'object' &&
        data != null &&
        'code' in data &&
        typeof data.code === 'string'
          ? data.code
          : undefined;
      const error = new Error(message) as Error & { code?: string };
      error.code = code;
      throw error;
    }
    markGameViewedInCache(gameId);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return;
    }
    throw error;
  }
};

export const fetchViewedGameIds = async (
  gameIds: string[],
): Promise<Set<string>> => {
  const unique = [...new Set(gameIds.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) {
    return new Set();
  }

  try {
    const { data, status } = await replayApi.post<{ viewedGameIds?: string[] }>(
      '/views/check',
      { gameIds: unique },
      {
        skipGlobalErrorToast: true,
        validateStatus: (statusCode) => statusCode === 200 || statusCode === 401,
      },
    );
    if (status === 401) {
      return new Set();
    }
    return new Set(
      Array.isArray(data?.viewedGameIds)
        ? data.viewedGameIds.filter((id) => typeof id === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
};
